import { useEffect, useRef } from "react";

interface CMSContainerProps {
    html: string;
}

export function CMSContainer({ html }: CMSContainerProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!containerRef.current || !html) return;

        // 1. Inject HTML (Scripts are dormant)
        containerRef.current.innerHTML = html;

        // 2. Find all script tags in the injected HTML
        const rawScripts = Array.from(containerRef.current.querySelectorAll("script"));

        // 3. Function to run a single script and return a Promise
        const runScript = (script: HTMLScriptElement) => {
            return new Promise<void>((resolve, reject) => {
                const newScript = document.createElement("script");

                // Copy all attributes (src, id, type, etc.)
                Array.from(script.attributes).forEach((attr) => {
                    newScript.setAttribute(attr.name, attr.value);
                });

                // Copy inline content
                if (script.innerHTML) {
                    newScript.textContent = script.innerHTML;
                }

                // IF it has a SRC (External file), we must wait for it to load
                if (script.src) {
                    newScript.onload = () => resolve();
                    newScript.onerror = () => {
                        console.error("Failed to load external script:", script.src);
                        resolve(); // Resolve anyway to keep the chain moving
                    };
                } else {
                    // Inline script? Resolve immediately after insertion
                    resolve();
                }

                // Replace the old dormant script with the new active one
                script.parentNode?.replaceChild(newScript, script);

                // If it was inline, we manually trigger resolve since onload won't fire
                if (!script.src) {
                    resolve();
                }
            });
        };

        // 4. Run the scripts strictly in order (Sequential Execution)
        const runSequentially = async () => {
            for (const script of rawScripts) {
                await runScript(script);
            }
        };

        runSequentially();

    }, [html]);

    return <div ref={containerRef} className="cms-content-wrapper" />;
}